import React, {
    useEffect,
    useRef,
    useState
} from "react";

import {
    IonApp,
    setupIonicReact
} from "@ionic/react";

import {
    checkForAppUpdate,
    clearSavedAppVersion
} from "./services/appVersion";

import Tabs from "./pages/Tabs";

import AuthBootstrap from
    "./components/AuthBootstrap";

/* Core CSS required for Ionic components to work properly */
import "@ionic/react/css/core.css";

/* Basic CSS for apps built with Ionic */
import "@ionic/react/css/normalize.css";
import "@ionic/react/css/structure.css";
import "@ionic/react/css/typography.css";

/* Optional CSS utils */
import "@ionic/react/css/padding.css";
import "@ionic/react/css/float-elements.css";
import "@ionic/react/css/text-alignment.css";
import "@ionic/react/css/text-transformation.css";
import "@ionic/react/css/flex-utils.css";
import "@ionic/react/css/display.css";

/* Theme variables */
import "./theme/variables.css";
import { IonReactRouter } from "@ionic/react-router";

setupIonicReact();

function waitForWaitingServiceWorker(
    registration: ServiceWorkerRegistration,
    timeoutMilliseconds = 15000
): Promise<ServiceWorker | null> {
    if (registration.waiting) {
        return Promise.resolve(registration.waiting);
    }

    return new Promise(function (resolve) {
        let completed = false;

        function finish(
            worker: ServiceWorker | null
        ): void {
            if (completed) {
                return;
            }

            completed = true;

            registration.removeEventListener(
                "updatefound",
                onUpdateFound
            );

            window.clearTimeout(timeoutId);

            resolve(worker);
        }

        function watchInstallingWorker(
            worker: ServiceWorker
        ): void {
            worker.addEventListener(
                "statechange",
                function () {
                    if (
                        worker.state === "installed" &&
                        registration.waiting
                    ) {
                        finish(registration.waiting);
                    }
                }
            );
        }

        function onUpdateFound(): void {
            const installingWorker =
                registration.installing;

            if (installingWorker) {
                watchInstallingWorker(
                    installingWorker
                );
            }
        }

        registration.addEventListener(
            "updatefound",
            onUpdateFound
        );

        /*
            Puede que el worker ya estuviera instalándose antes
            de agregar el listener updatefound.
        */
        if (registration.installing) {
            watchInstallingWorker(
                registration.installing
            );
        }

        const timeoutId = window.setTimeout(
            function () {
                finish(
                    registration.waiting ?? null
                );
            },
            timeoutMilliseconds
        );
    });
}

const App: React.FC = () => {
    const [isUpdating, setIsUpdating] =
        useState(false);

    const hasReloadedForNewServiceWorker =
        useRef(false);

    /*
        Evita dos activaciones simultáneas, especialmente porque
        React.StrictMode ejecuta efectos dos veces en desarrollo.
    */
    const isUpdatingRef = useRef(false);

    async function forceAppUpdate(): Promise<void> {
        if (isUpdatingRef.current || isUpdating) {
            return;
        }

        isUpdatingRef.current = true;
        setIsUpdating(true);

try {
    if (!("serviceWorker" in navigator)) {
        return;
    }

    const registration =
        await navigator.serviceWorker.getRegistration();

    if (!registration) {
        /*
            Primera carga o registro todavía pendiente.
        */
        return;
    }

    await registration.update();

    const waitingWorker =
        await waitForWaitingServiceWorker(
            registration
        );

    if (!waitingWorker) {
        console.warn(
            "La nueva versión aún no termina de descargarse."
        );

        return;
    }

    waitingWorker.postMessage({
        type: "SKIP_WAITING"
    });
} catch (error) {
    console.error(
        "No fue posible forzar la actualización:",
        error
    );
} finally {
            isUpdatingRef.current = false;
            setIsUpdating(false);
        }
    }

    useEffect(function () {
        let active = true;

        async function verifyUpdate(): Promise<void> {
            try {
                const hasUpdate =
                    await checkForAppUpdate();

                if (!active || !hasUpdate) {
                    return;
                }

                await forceAppUpdate();
            } catch (error) {
                console.error(
                    "No fue posible verificar la actualización:",
                    error
                );
            }
        }

        void verifyUpdate();

        const intervalId = window.setInterval(
            function () {
                void verifyUpdate();
            },
            5 * 60 * 1000
        );

        return function () {
            active = false;
            window.clearInterval(intervalId);
        };
    }, []);

    useEffect(function () {
        function reloadAfterServiceWorkerChange(): void {
            if (
                hasReloadedForNewServiceWorker.current
            ) {
                return;
            }

            hasReloadedForNewServiceWorker.current =
                true;

            clearSavedAppVersion();

            /*
                Sólo se recarga cuando el nuevo Service Worker
                ya controla la pestaña.
            */
            window.location.reload();
        }

        navigator.serviceWorker?.addEventListener(
            "controllerchange",
            reloadAfterServiceWorkerChange
        );

        return function () {
            navigator.serviceWorker?.removeEventListener(
                "controllerchange",
                reloadAfterServiceWorkerChange
            );
        };
    }, []);

    return (
        <IonApp>
            <AuthBootstrap />

            <IonReactRouter>
                <Tabs />
            </IonReactRouter>
        </IonApp>
    );
};

export default App;